import {
  createFileRoute,
  Link,
  Outlet,
  useChildMatches,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { FileText, Plus } from "lucide-react";
import { useAuth } from "@/contexts/auth-context";
import type { PurchaseRequestListItemDto } from "@procureflow/shared-types";
import {
  TransactionListShell,
  type ColumnDef,
  type StatusTab,
} from "@/components/transaction-list-shell";
import { TransactionStatusBadge } from "@/components/transaction-status-badge";
import { PR_STATUS_CONFIG } from "@/lib/status-config";
import { formatAmount, formatDate } from "@/lib/format";
import { ExportCsvButton } from "@/components/export-csv-button";

export const Route = createFileRoute("/purchase-requests")({
  head: () => ({
    meta: [
      { title: "Purchase Requests — ProcureFlow" },
      {
        name: "description",
        content: "View and track purchase requests.",
      },
    ],
  }),
  component: function PRPageOrOutlet() {
    const child = useChildMatches();
    return child.length > 0 ? <Outlet /> : <PRListPage />;
  },
});

const STATUS_TABS: StatusTab[] = [
  { label: "All", value: null },
  { label: "Draft", value: "Draft" },
  { label: "Submitted", value: "Submitted" },
  { label: "Approved", value: "Approved" },
  { label: "Rejected", value: "Rejected" },
  { label: "Changes Requested", value: "ChangesRequested" },
];

const COLUMNS: ColumnDef<PurchaseRequestListItemDto>[] = [
  {
    id: "pr_number",
    header: "PR Number",
    cell: (pr) => (
      <Link to="/purchase-requests/$id" params={{ id: pr.id }} className="flex items-center gap-3">
        <div className="h-8 w-8 rounded-lg bg-primary/10 grid place-items-center text-primary shrink-0">
          <FileText className="h-3.5 w-3.5" />
        </div>
        <span className="font-mono text-xs font-medium hover:text-primary transition-colors">
          {pr.pr_number}
        </span>
      </Link>
    ),
  },
  {
    id: "title",
    header: "Title",
    cell: (pr) => <span className="font-medium truncate max-w-[280px] block">{pr.title}</span>,
  },
  {
    id: "amount",
    header: "Amount",
    headerClassName: "text-right",
    cellClassName: "text-right font-semibold tabular-nums",
    cell: (pr) => formatAmount(pr.total_amount),
  },
  {
    id: "status",
    header: "Status",
    cell: (pr) => <TransactionStatusBadge config={PR_STATUS_CONFIG[pr.status]} />,
  },
  {
    id: "created",
    header: "Created",
    cellClassName: "text-muted-foreground",
    cell: (pr) => formatDate(pr.created_at),
  },
  {
    id: "required_by",
    header: "Required By",
    cellClassName: "text-muted-foreground",
    cell: (pr) => formatDate(pr.required_by_date),
  },
];

function PRListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Sync the Export CSV query string with the list's active status filter
  // so the export reflects whatever is currently visible. `useSearch` is
  // safe to call without a typed schema here — we only read `status`.
  const search = useSearch({ strict: false }) as Record<string, string | undefined>;
  const exportSearchParams = search.status
    ? new URLSearchParams({ status: search.status }).toString()
    : undefined;

  return (
    <TransactionListShell<PurchaseRequestListItemDto>
      title="Purchase Requests"
      countNoun={{ singular: "request", plural: "requests" }}
      endpoint="/api/purchase-requests"
      tabs={STATUS_TABS}
      columns={COLUMNS}
      rowKey={(pr) => pr.id}
      rowClassName="cursor-pointer hover:bg-muted/30 transition-colors"
      onRowClick={(pr) => navigate({ to: "/purchase-requests/$id", params: { id: pr.id } })}
      emptyIcon={FileText}
      emptyMessage="No purchase requests found."
      headerAction={
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <ExportCsvButton
            endpoint="/api/purchase-requests/export/csv"
            searchParams={exportSearchParams}
            fallbackFilename="purchase-requests.csv"
          />
          {user?.role === "Requester" ? (
            <Link
              to="/purchase-requests/new"
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              <Plus className="h-4 w-4" />
              New Request
            </Link>
          ) : null}
        </div>
      }
    />
  );
}
